import { FC, useState, useEffect, useRef, useMemo, useDeferredValue } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { toast } from 'react-hot-toast'
import { WorkflowRunGroup, RunStatus } from '@aero-platform/shared'
import { useGenerateBlobSasUrlDownload } from '../../api/upload-API'
import {
  downloadSingleCaseLog,
  getCaseNumber,
  listCaseLogFiles
} from '../../services/logDownloadService'
import { RunStatusCircle } from './StatusCircle'

// Rendering a multi-MB string in one <pre> blocks the main thread and freezes the page.
const MAX_RENDERED_LINES = 3000

// A fetch older than this no longer blocks the next refresh tick.
const STALE_TICK_MS = 15000

interface LogViewerModalProps {
  visible: boolean
  workflowRunGroup: WorkflowRunGroup
  raceTeamId: string
  onHide: () => void
}

const LogViewerModal: FC<LogViewerModalProps> = ({
  visible,
  workflowRunGroup,
  raceTeamId,
  onHide
}) => {
  const [, generateSasUrl] = useGenerateBlobSasUrlDownload()
  const [logContent, setLogContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [autoScroll, setAutoScroll] = useState(true)
  const [wrapLines, setWrapLines] = useState(true)
  const [filter, setFilter] = useState('')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [discoveredLogFiles, setDiscoveredLogFiles] = useState<string[]>([])
  const [selectedLogFile, setSelectedLogFile] = useState<string | null>(null)
  const [discoveringLogs, setDiscoveringLogs] = useState(false)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const logEndRef = useRef<HTMLDivElement>(null)
  const logContainerRef = useRef<HTMLDivElement>(null)

  const caseNumber = getCaseNumber(workflowRunGroup)
  const caseName = workflowRunGroup.name
  const status = workflowRunGroup.overallStatus

  // Dev-only hook for scripts/emulate-log-updates.ps1; CRA inlines NODE_ENV so this is
  // dead code in a production build.
  const forceStream =
    process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_LOG_VIEWER_FORCE_STREAM === 'true'

  // Determine if case is in a final state
  const isFinalState =
    !forceStream &&
    (status === RunStatus.COMPLETED || status === RunStatus.ERROR || status === RunStatus.CANCELLED)

  // Get status display information
  const getStatusInfo = () => {
    switch (status) {
      case RunStatus.PENDING:
      case RunStatus.SUBMITTED:
        return { label: 'Queued', severity: 'info' as const, icon: 'pi-clock' }
      case RunStatus.WAITING:
        return { label: 'Starting', severity: 'warning' as const, icon: 'pi-spinner' }
      case RunStatus.RUNNING:
        return { label: 'Running', severity: 'info' as const, icon: 'pi-play' }
      case RunStatus.COMPLETED:
        return { label: 'Completed', severity: 'success' as const, icon: 'pi-check' }
      case RunStatus.ERROR:
        return { label: 'Error', severity: 'danger' as const, icon: 'pi-times' }
      case RunStatus.CANCELLED:
        return { label: 'Cancelled', severity: 'warning' as const, icon: 'pi-ban' }
      default:
        return { label: 'Unknown', severity: 'secondary' as const, icon: 'pi-question' }
    }
  }

  const statusInfo = getStatusInfo()

  // Discover available log files
  const discoverLogFiles = async () => {
    if (!caseNumber) {
      console.log('[LogViewer] Cannot discover logs - no case number')
      return
    }

    console.log('[LogViewer] Discovering log files', {
      caseNumber,
      raceTeamId,
      timestamp: new Date().toISOString()
    })

    setDiscoveringLogs(true)

    try {
      const files = await listCaseLogFiles(generateSasUrl, raceTeamId, caseNumber)

      console.log('[LogViewer] Log files discovered', {
        count: files.length,
        files
      })

      setDiscoveredLogFiles(files)

      // Auto-select the first log file if available
      if (files.length > 0 && !selectedLogFile) {
        const firstLog = files[0]
        console.log('[LogViewer] Auto-selecting first log file:', firstLog)
        setSelectedLogFile(firstLog)
      } else if (files.length === 0) {
        console.log('[LogViewer] No log files found')
      }
    } catch (error: any) {
      console.error('[LogViewer] Error discovering log files:', {
        error: error?.message,
        stack: error?.stack
      })
      // Don't set error state here - let the log loading handle that
    } finally {
      setDiscoveringLogs(false)
    }
  }

  const tickStartedAtRef = useRef<number | null>(null)
  const loadSeqRef = useRef(0)

  // Load log content
  const loadLog = async (isRetry = false) => {
    if (!caseNumber || isPaused) {
      console.log('[LogViewer] Skipping load - caseNumber:', caseNumber, 'isPaused:', isPaused)
      return
    }

    if (!selectedLogFile) {
      console.log('[LogViewer] Skipping load - no log file selected yet')
      return
    }

    console.log('[LogViewer] Starting log fetch', {
      isRetry,
      caseNumber,
      raceTeamId,
      selectedLogFile,
      status: statusInfo.label,
      timestamp: new Date().toISOString()
    })

    // A newer fetch (or file switch) makes this one's result stale.
    const seq = ++loadSeqRef.current

    if (!isRetry) {
      setLoading(true)
    }
    setError(null)

    try {
      console.log('[LogViewer] Calling downloadSingleCaseLog...')
      const blob = await downloadSingleCaseLog(
        generateSasUrl,
        raceTeamId,
        caseNumber,
        selectedLogFile
      )

      console.log('[LogViewer] Blob received', {
        size: blob.size,
        type: blob.type
      })

      const content = await blob.text()
      if (seq !== loadSeqRef.current) return
      console.log('[LogViewer] Log content loaded', { contentLength: content.length })

      // Identical content would still re-render and re-scroll the whole log.
      setLogContent(prev => (prev === content ? prev : content))
      setLastUpdated(new Date())
      setRetryCount(0) // Reset retry count on success

      console.log('[LogViewer] Successfully updated log content')
    } catch (error: any) {
      if (seq !== loadSeqRef.current) return
      console.error('[LogViewer] Error loading log:', {
        error: error?.message,
        stack: error?.stack,
        caseNumber,
        raceTeamId,
        selectedLogFile
      })

      // If log doesn't exist yet and case is still running, show waiting state
      if (
        !isFinalState &&
        (error?.message?.includes('404') || error?.message?.includes('not found'))
      ) {
        console.log(
          '[LogViewer] Log not found (404) - entering waiting state, retry count:',
          retryCount + 1
        )
        setError('Waiting for log...')
        setRetryCount(prev => prev + 1)
      } else {
        console.error('[LogViewer] Fatal error loading log:', error?.message)
        setError(error?.message || 'Failed to load log file')
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false)
    }
  }

  // The refresh interval is set up before a log file is selected, so it must call the
  // latest closures rather than the ones captured when the interval was created.
  // isInitial shows the loading spinner; refresh ticks update in place instead.
  const streamTick = async (isInitial = false) => {
    // Skip overlapping ticks, but a stalled request (or a file switch) must not block refresh.
    const inFlightSince = tickStartedAtRef.current
    if (!isInitial && inFlightSince !== null && Date.now() - inFlightSince < STALE_TICK_MS) return
    const startedAt = Date.now()
    tickStartedAtRef.current = startedAt
    try {
      // A queued case often has no log files yet when the modal opens; keep re-discovering
      // until one shows up, otherwise the viewer would stay empty forever.
      if (!selectedLogFile) {
        await discoverLogFiles()
        return
      }
      await loadLog(!isInitial)
    } finally {
      if (tickStartedAtRef.current === startedAt) tickStartedAtRef.current = null
    }
  }

  const streamTickRef = useRef(streamTick)
  streamTickRef.current = streamTick

  // Discover log files when modal opens
  useEffect(() => {
    if (visible && caseNumber) {
      console.log('[LogViewer] Modal opened, discovering log files')
      discoverLogFiles()
    }
  }, [visible, caseNumber])

  // Initialize - load log when a file is selected
  useEffect(() => {
    if (visible && caseNumber && selectedLogFile) {
      console.log('[LogViewer] Log file selected, initiating first log load')
      // Go through the tick so the initial fetch shares the in-flight guard with auto-refresh.
      streamTickRef.current(true)
    }
  }, [visible, caseNumber, selectedLogFile])

  // Auto-scroll to bottom when content updates
  useEffect(() => {
    if (autoScroll && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
  }, [logContent, autoScroll])

  // Auto-refresh with 5s interval (streaming)
  useEffect(() => {
    if (visible && !isPaused && !isFinalState) {
      console.log('[LogViewer] Starting auto-refresh interval (5 seconds)')
      intervalRef.current = setInterval(() => {
        console.log('[LogViewer] Auto-refresh triggered')
        streamTickRef.current()
      }, 5000) // 5 second interval for streaming
    } else {
      console.log('[LogViewer] Auto-refresh stopped', {
        visible,
        isPaused,
        isFinalState
      })
    }

    return () => {
      if (intervalRef.current) {
        console.log('[LogViewer] Clearing auto-refresh interval')
        clearInterval(intervalRef.current)
      }
    }
  }, [visible, isPaused, isFinalState])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  // Handle scroll - pause auto-scroll if user scrolls up
  const handleScroll = () => {
    if (!logContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50

    if (!isAtBottom && autoScroll) {
      // User scrolled up, but we'll keep auto-scroll on - they can toggle it off
    }
  }

  // Filtering and line-splitting run off the urgent render path so typing stays responsive.
  const deferredFilter = useDeferredValue(filter)
  const { displayContent, hiddenLines } = useMemo(() => {
    const term = deferredFilter.trim().toLowerCase()
    let lines = logContent.split('\n')
    if (term) {
      lines = lines.filter(line => line.toLowerCase().includes(term))
    }
    const hidden = Math.max(0, lines.length - MAX_RENDERED_LINES)
    return {
      displayContent: hidden > 0 ? lines.slice(-MAX_RENDERED_LINES).join('\n') : lines.join('\n'),
      hiddenLines: hidden
    }
  }, [logContent, deferredFilter])

  // Copy all log content to clipboard
  const handleCopyAll = () => {
    navigator.clipboard
      .writeText(logContent)
      .then(() => toast.success('Log copied to clipboard'))
      .catch(() => toast.error('Failed to copy log'))
  }

  // Download full log
  const handleDownload = () => {
    if (!logContent) {
      toast.error('No log content to download')
      return
    }

    const blob = new Blob([logContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${caseName}_${selectedLogFile || 'log.txt'}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    toast.success('Log file downloaded')
  }

  // Pop out to new window with live updates
  const handlePopOut = () => {
    if (!caseNumber) {
      toast.error('Case number not available')
      return
    }

    const newWindow = window.open('', '_blank', 'width=1200,height=800')
    if (!newWindow) {
      toast.error('Failed to open new window. Please check your popup blocker.')
      return
    }

    // Get the API base URL (for dev it's localhost:3001, for prod it's the same origin)
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin

    // Get auth tokens from localStorage to pass to popout window
    const accessToken = localStorage.getItem('ACCESS_TOKEN') || ''
    const raceTeamIdFromStorage = localStorage.getItem('GMMS_RACE_TEAM_ID') || raceTeamId

    // Build the storage path for the log file
    const buildLogPath = (teamId: string, caseNum: string) => {
      // Strip the "0-" prefix if present (systemRunId format is "0-105", but storage path is "cases/105/Logs")
      // Note: Don't include container name or raceTeamId - the backend adds those
      const cleanCaseNum = caseNum.startsWith('0-') ? caseNum.substring(2) : caseNum
      return `cases/${cleanCaseNum}/Logs`
    }

    const logPath = buildLogPath(raceTeamId, caseNumber)

    // Get status color based on RunStatus
    const getStatusColor = (status: RunStatus) => {
      switch (status) {
        case RunStatus.PENDING:
        case RunStatus.SUBMITTED:
          return '#6366f1' // blue
        case RunStatus.WAITING:
          return '#f59e0b' // orange
        case RunStatus.RUNNING:
          return '#3b82f6' // blue
        case RunStatus.COMPLETED:
          return '#10b981' // green
        case RunStatus.ERROR:
          return '#ef4444' // red
        case RunStatus.CANCELLED:
          return '#f59e0b' // orange
        default:
          return '#6b7280' // gray
      }
    }

    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Live Log - ${caseName}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #1e1e1e;
              color: #d4d4d4;
              display: flex;
              flex-direction: column;
              height: 100vh;
              overflow: hidden;
            }
            .header {
              background: #252526;
              border-bottom: 1px solid #3e3e42;
              padding: 12px 16px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              flex-shrink: 0;
            }
            .title {
              font-size: 16px;
              font-weight: 600;
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .status-indicator {
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background: ${getStatusColor(status)};
              display: inline-block;
            }
            .info {
              display: flex;
              align-items: center;
              gap: 16px;
              font-size: 12px;
              color: #858585;
            }
            .toolbar {
              background: #252526;
              border-bottom: 1px solid #3e3e42;
              padding: 8px 16px;
              display: flex;
              align-items: center;
              gap: 8px;
              flex-shrink: 0;
            }
            button {
              background: #0e639c;
              color: white;
              border: none;
              padding: 6px 12px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 13px;
              display: flex;
              align-items: center;
              gap: 6px;
              transition: background 0.2s;
            }
            button:hover {
              background: #1177bb;
            }
            button:disabled {
              background: #3e3e42;
              color: #858585;
              cursor: not-allowed;
            }
            button.secondary {
              background: #3e3e42;
            }
            button.secondary:hover {
              background: #4e4e52;
            }
            .divider {
              width: 1px;
              height: 20px;
              background: #3e3e42;
              margin: 0 4px;
            }
            .log-container {
              flex: 1;
              overflow: auto;
              padding: 16px;
              font-family: 'Courier New', monospace;
              font-size: 13px;
              white-space: pre-wrap;
              word-wrap: break-word;
            }
            .log-container.no-wrap {
              white-space: pre;
            }
            .loading {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
              font-size: 14px;
              color: #858585;
            }
            .error {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
              flex-direction: column;
              gap: 12px;
              color: #ef4444;
            }
            .spinner {
              border: 3px solid #3e3e42;
              border-top: 3px solid #0e639c;
              border-radius: 50%;
              width: 32px;
              height: 32px;
              animation: spin 1s linear infinite;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">
              <span class="status-indicator" id="statusIndicator"></span>
              <span>${caseName}</span>
              <span style="font-size: 12px; font-family: monospace; background: #3e3e42; padding: 4px 8px; border-radius: 4px; margin-left: 8px;">${
                selectedLogFile || 'log.txt'
              }</span>
              <span id="statusText" style="font-size: 13px; font-weight: 500;">${
                statusInfo.label
              }</span>
            </div>
            <div class="info">
              <span id="lastUpdate">Never updated</span>
              <span id="refreshStatus">⏸️ Paused</span>
            </div>
          </div>
          <div class="toolbar">
            <button id="pauseBtn" onclick="togglePause()">
              <span id="pauseIcon">▶️</span>
              <span id="pauseText">Resume</span>
            </button>
            <div class="divider"></div>
            <button onclick="copyAll()" class="secondary">📋 Copy All</button>
            <button onclick="downloadLog()" class="secondary">⬇️ Download</button>
            <div class="divider"></div>
            <button onclick="toggleWrap()" class="secondary" id="wrapBtn">
              <span id="wrapText">Wrap Lines: ON</span>
            </button>
            <button onclick="toggleAutoScroll()" class="secondary" id="autoScrollBtn">
              <span id="autoScrollText">Auto-scroll: ON</span>
            </button>
          </div>
          <div class="log-container" id="logContainer">
            <div class="loading">
              <div class="spinner"></div>
              <span style="margin-left: 12px;">Loading log...</span>
            </div>
          </div>
          <script>
            let isPaused = false;
            let autoScroll = true;
            let wrapLines = true;
            let intervalId = null;
            let logContent = '';
            const API_BASE = '${apiBaseUrl}';
            const ACCESS_TOKEN = '${accessToken}';
            const RACE_TEAM_ID = '${raceTeamIdFromStorage}';
            const CASE_NUMBER = '${caseNumber}';
            const LOG_FILE = '${selectedLogFile || 'log.txt'}';
            const LOG_PATH = '${logPath}/' + LOG_FILE;
            const CASE_NAME = '${caseName}';
            
            async function fetchLog() {
              if (isPaused) return;
              
              try {
                // Download blob via backend proxy (avoids CORS)
                const headers = {
                  'Content-Type': 'application/json'
                };
                
                // Add auth headers if available
                if (ACCESS_TOKEN) {
                  headers['Authorization'] = 'Bearer ' + ACCESS_TOKEN;
                }
                if (RACE_TEAM_ID) {
                  headers['gmmsraceteamid'] = RACE_TEAM_ID;
                }
                
                const response = await fetch(API_BASE + '/DownloadBlobProxy', {
                  method: 'POST',
                  headers: headers,
                  body: JSON.stringify({
                    uploadType: 'CASE_LOG_FILE',
                    blobPath: LOG_PATH,
                    raceTeamId: RACE_TEAM_ID
                  })
                });
                
                if (!response.ok) {
                  const errorText = await response.text();
                  console.error('Error response:', response.status, errorText);
                  throw new Error(\`HTTP \${response.status}: \${errorText.substring(0, 100)}\`);
                }
                
                // Get the blob and convert to text
                const blob = await response.blob();
                const content = await blob.text();
                logContent = content;
                
                const container = document.getElementById('logContainer');
                container.textContent = content || 'Log is empty';
                
                // Update last update time
                const now = new Date();
                document.getElementById('lastUpdate').textContent = 
                  'Last updated: ' + now.toLocaleTimeString();
                
                // Auto-scroll if enabled
                if (autoScroll) {
                  container.scrollTop = container.scrollHeight;
                }
              } catch (error) {
                console.error('Error fetching log:', error);
                const container = document.getElementById('logContainer');
                if (!logContent) {
                  container.innerHTML = \`
                    <div class="error">
                      <span style="font-size: 32px;">⚠️</span>
                      <span>Error loading log: \${error.message}</span>
                      <span style="font-size: 12px; color: #858585;">Retrying...</span>
                    </div>
                  \`;
                }
              }
            }
            
            function togglePause() {
              isPaused = !isPaused;
              document.getElementById('pauseIcon').textContent = isPaused ? '▶️' : '⏸️';
              document.getElementById('pauseText').textContent = isPaused ? 'Resume' : 'Pause';
              document.getElementById('refreshStatus').textContent = isPaused ? '⏸️ Paused' : '🔄 Streaming';
              
              if (!isPaused && !intervalId) {
                startStreaming();
              }
            }
            
            function toggleWrap() {
              wrapLines = !wrapLines;
              const container = document.getElementById('logContainer');
              if (wrapLines) {
                container.classList.remove('no-wrap');
                document.getElementById('wrapText').textContent = 'Wrap Lines: ON';
              } else {
                container.classList.add('no-wrap');
                document.getElementById('wrapText').textContent = 'Wrap Lines: OFF';
              }
            }
            
            function toggleAutoScroll() {
              autoScroll = !autoScroll;
              document.getElementById('autoScrollText').textContent = 
                autoScroll ? 'Auto-scroll: ON' : 'Auto-scroll: OFF';
            }
            
            function copyAll() {
              navigator.clipboard.writeText(logContent)
                .then(() => alert('Log copied to clipboard!'))
                .catch(() => alert('Failed to copy log'));
            }
            
            function downloadLog() {
              const blob = new Blob([logContent], { type: 'text/plain' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = CASE_NAME + '_' + LOG_FILE;
              a.click();
              window.URL.revokeObjectURL(url);
            }
            
            function startStreaming() {
              fetchLog(); // Initial fetch
              intervalId = setInterval(fetchLog, 5000); // Update every 5 seconds
            }
            
            // Start streaming when page loads
            startStreaming();
            
            // Cleanup on window close
            window.addEventListener('beforeunload', () => {
              if (intervalId) {
                clearInterval(intervalId);
              }
            });
          </script>
        </body>
      </html>
    `)
    newWindow.document.close()

    toast.success('Log viewer opened in new window')
    onHide() // Close the modal after popping out
  }

  // Status header (sticky)
  const statusHeader = (
    <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <h3 className="text-lg font-semibold">{caseName}</h3>
        {selectedLogFile && (
          <span className="text-sm text-gray-500 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
            {selectedLogFile}
          </span>
        )}
        <div className="flex items-center">
          <RunStatusCircle status={status} />
          <span className="ml-1 text-sm font-medium">{statusInfo.label}</span>
        </div>
      </div>
      {lastUpdated && !error && (
        <span className="text-xs text-gray-500">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </span>
      )}
    </div>
  )

  // Toolbar
  const toolbar = (
    <div className="border-b border-gray-200 dark:border-gray-700 p-2 flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center space-x-2">
        <Button
          icon={isPaused ? 'pi pi-play' : 'pi pi-pause'}
          label={isPaused ? 'Resume' : 'Pause'}
          onClick={() => setIsPaused(!isPaused)}
          disabled={isFinalState}
          className="p-button-sm p-button-text"
          tooltip={isPaused ? 'Resume streaming' : 'Pause streaming'}
          pt={{
            icon: { className: 'mr-2' }
          }}
        />
        <div className="h-6 w-px bg-gray-300"></div>
        <Button
          icon="pi pi-copy"
          label="Copy All"
          onClick={handleCopyAll}
          disabled={!logContent}
          className="p-button-sm p-button-text"
          pt={{
            icon: { className: 'mr-2' }
          }}
        />
        <Button
          icon="pi pi-download"
          label="Download"
          onClick={handleDownload}
          disabled={!logContent}
          className="p-button-sm p-button-text"
          pt={{
            icon: { className: 'mr-2' }
          }}
        />
        <Button
          icon="pi pi-window-maximize"
          label="Pop Out"
          onClick={handlePopOut}
          className="p-button-sm p-button-text"
          tooltip="Open in new window with live updates"
          pt={{
            icon: { className: 'mr-2' }
          }}
        />
      </div>
      <div className="flex items-center space-x-2">
        {discoveredLogFiles.length > 0 && (
          <>
            <Dropdown
              value={selectedLogFile}
              options={discoveredLogFiles.map(file => ({ label: file, value: file }))}
              onChange={e => setSelectedLogFile(e.value)}
              placeholder="Select log file"
              className="p-inputtext-sm w-64"
              disabled={discoveringLogs}
            />
            <div className="h-6 w-px bg-gray-300"></div>
          </>
        )}
        <span className="p-input-icon-left">
          <i className="pi pi-search ml-2" />
          <InputText
            placeholder="Filter..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="p-inputtext-sm w-48 pl-8"
          />
        </span>
        <Button
          icon={wrapLines ? 'pi pi-check-square' : 'pi pi-square'}
          label="Wrap Lines"
          onClick={() => setWrapLines(!wrapLines)}
          className="p-button-sm p-button-text"
          pt={{
            icon: { className: 'mr-2' }
          }}
        />
        <Button
          icon={autoScroll ? 'pi pi-check-square' : 'pi pi-square'}
          label="Auto-scroll"
          onClick={() => setAutoScroll(!autoScroll)}
          className="p-button-sm p-button-text"
          pt={{
            icon: { className: 'mr-2' }
          }}
        />
      </div>
    </div>
  )

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={null}
      style={{ width: '90vw', height: '85vh' }}
      modal
      className="log-viewer-modal"
      contentClassName="p-0">
      {statusHeader}
      {toolbar}
      <div
        ref={logContainerRef}
        onScroll={handleScroll}
        className="overflow-auto"
        style={{ height: 'calc(85vh - 180px)' }}>
        {loading && !logContent ? (
          <div className="flex items-center justify-center h-full">
            <ProgressSpinner
              aria-label="Loading log"
              pt={{
                circle: { className: '!stroke-dark-blue' }
              }}
            />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full flex-col space-y-4">
            {error === 'Waiting for log...' ? (
              <>
                <ProgressSpinner
                  aria-label="Waiting for log"
                  pt={{
                    circle: { className: '!stroke-blue-500' }
                  }}
                />
                <p className="text-gray-500">{error}</p>
                <p className="text-sm text-gray-400">
                  The log file will appear shortly after the job starts
                </p>
                {retryCount > 0 && (
                  <p className="text-xs text-gray-400">Auto-retry attempt {retryCount}...</p>
                )}
              </>
            ) : (
              <>
                <div className="text-red-500">
                  <i className="pi pi-exclamation-triangle text-4xl"></i>
                </div>
                <p className="text-lg font-semibold text-red-600">Error loading log</p>
                <p className="text-sm text-gray-600">{error}</p>
                <Button
                  label="Retry"
                  icon="pi pi-refresh"
                  onClick={() => loadLog()}
                  className="p-button-sm"
                />
              </>
            )}
          </div>
        ) : !logContent ? (
          <div className="flex items-center justify-center h-full flex-col space-y-4">
            <div className="text-gray-400">
              <i className="pi pi-inbox text-4xl"></i>
            </div>
            <p className="text-gray-500">Searching for log content...</p>
          </div>
        ) : (
          <div className="p-4 bg-gray-900 text-gray-100 font-mono text-sm">
            {hiddenLines > 0 && (
              <div className="text-xs text-gray-400 mb-2">
                Showing the last {MAX_RENDERED_LINES.toLocaleString()} lines (
                {hiddenLines.toLocaleString()} earlier lines hidden). Use Download or Copy All for
                the full log.
              </div>
            )}
            <pre
              className={wrapLines ? 'whitespace-pre-wrap' : 'whitespace-pre'}
              style={{ margin: 0 }}>
              {displayContent || 'No matching lines'}
            </pre>
            <div ref={logEndRef} />
          </div>
        )}
      </div>
    </Dialog>
  )
}

export default LogViewerModal
